import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase';
import lang from '../language/Eng_Aust.json';

const PageContainer = styled.div`
  box-sizing: border-box;
  height: 100vh;
  height: 100dvh;
  background-color: #FDFBF9;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const LoginCard = styled.div`
  box-sizing: border-box;
  background: white;
  border-radius: 24px;
  padding: 48px;
  width: 100%;
  max-width: 440px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.03);
  border: 1px solid rgba(0,0,0,0.05);

  @media (max-width: 768px) {
    padding: 32px 24px;
  }
`;

const PillBadge = styled.div`
  background-color: #FFF2D1;
  color: #937A3B;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  display: inline-block;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 32px;
  color: #1A1A1A;
  margin: 0 0 12px 0;
  font-weight: 800;
  letter-spacing: -0.5px;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #666;
  margin: 0 0 32px 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 16px;
  border: 1px solid #EAEAEA;
  border-radius: 12px;
  font-size: 15px;
  background-color: #FAFAFA;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #F8E287;
    background-color: #FFF;
    box-shadow: 0 0 0 3px rgba(248, 226, 135, 0.2);
  }
`;

const ErrorMessage = styled.div`
  color: #E53E3E;
  font-size: 14px;
  margin-top: 8px;
  text-align: left;
`;

const LoginButton = styled.button`
  background-color: #FCE17A;
  color: #1A1A1A;
  border: none;
  border-radius: 12px;
  padding: 16px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  margin-top: 12px;
  transition: all 0.2s ease;

  &:hover {
    background-color: #F8D654;
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background-color: #F0F0F0;
    color: #AAA;
    cursor: not-allowed;
    transform: none;
  }
`;

const DeliveryLogin: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // 매니저 계정인 경우 확인 페이지로 이동, 그 외에는 주문 페이지로 이동
      if (userCredential.user.email === 'butkitchen@butteredsydney.com.au') {
        navigate('/delivery/main');
      } else {
        navigate('/delivery/start');
      }
    } catch (err: any) {
      console.error(err);
      setError(lang.login.invalidCredentials);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <LoginCard>
        <div style={{ textAlign: 'center' }}>
          <PillBadge>{lang.login.staffOnly}</PillBadge>
          <Title>{lang.login.signIn}</Title>
          <Subtitle>{lang.login.welcomeMessage}</Subtitle>
        </div>

        <Form onSubmit={handleLogin}>
          <InputGroup>
            <Label>{lang.login.emailLabel}</Label>
            <Input 
              type="email" 
              placeholder={lang.login.emailPlaceholder} 
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
          </InputGroup>

          <InputGroup>
            <Label>{lang.login.passwordLabel}</Label>
            <Input 
              type="password" 
              placeholder={lang.login.passwordPlaceholder} 
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />
          </InputGroup>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <LoginButton type="submit" disabled={loading}>
            {loading ? lang.login.signingIn : lang.login.signIn}
          </LoginButton>
        </Form>
      </LoginCard>
    </PageContainer>
  );
};

export default DeliveryLogin;
